import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import NavigationScreen from './components/NavigationScreen';
import AuthScreen from './components/AuthScreen';
import WelcomeScreen from './components/WelcomeScreen';
import ScorcardForm from './components/ScorcardForm';
import ResultsScreen from './components/ResultsScreen';
import PublishingPathSelector from './components/PublishingPathSelector';
import PublishingResults from './components/PublishingResults';
import ActionPlanBuilder from './components/ActionPlanBuilder';
import ActionPlanResults from './components/ActionPlanResults';

import './App.css';

function App() {
  const [currentStep, setCurrentStep] = useState('auth');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Scorecard state
  const [scorecardData, setScorecardData] = useState(null);

  // Publishing Path state
  const [publishingResponses, setPublishingResponses] = useState({});
  const [publishingScores, setPublishingScores] = useState({ trad: 0, hybrid: 0, indie: 0 });
  const [recommendedPath, setRecommendedPath] = useState('');
  const [confidence, setConfidence] = useState('');

  // Action Plan state
  const [planResponses, setPlanResponses] = useState({});
  const [actionPlan, setActionPlan] = useState({});

  // Check if user is already authenticated on mount
  useEffect(() => {
    const authStatus = localStorage.getItem('manuscript-auth');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
      setCurrentStep('navigation');
    }
  }, []);

  const handleAuthenticated = () => {
    setIsAuthenticated(true);
    localStorage.setItem('manuscript-auth', 'true');
    setCurrentStep('navigation');
  };

  const handleNavigationSelect = (tool) => {
    switch (tool) {
      case 'scorecard':
        setCurrentStep('welcome');
        break;
      case 'publishing':
        setCurrentStep('publishing-selector');
        break;
      case 'action-plan':
        setCurrentStep('action-plan-builder');
        break;
      default:
        setCurrentStep('navigation');
    }
  };

  const handleStartScorecard = () => {
    setCurrentStep('scorecard');
  };

  const handleScorcardComplete = (data) => {
    setScorecardData(data);
    setCurrentStep('results');
  };

  const handlePublishingComplete = (formResponses, scores, path, conf) => {
    setPublishingResponses(formResponses);
    setPublishingScores(scores);
    setRecommendedPath(path);
    setConfidence(conf);
    setCurrentStep('publishing-results');
  };

  const handleActionPlanComplete = (formResponses, plan) => {
    setPlanResponses(formResponses);
    setActionPlan(plan);
    setCurrentStep('action-plan-results');
  };

  const handleRestart = () => {
    setCurrentStep('navigation');
    // Reset all states
    setScorecardData(null);
    setPublishingResponses({});
    setPublishingScores({ trad: 0, hybrid: 0, indie: 0 });
    setRecommendedPath('');
    setConfidence('');
    setPlanResponses({});
    setActionPlan({});
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('manuscript-auth');
    setCurrentStep('auth');
  };

  const renderContent = () => {
    switch (currentStep) {
      case 'auth':
        return <AuthScreen onAuthenticated={handleAuthenticated} />;
      case 'navigation':
        return <NavigationScreen onSelect={handleNavigationSelect} />;
      case 'welcome':
        return <WelcomeScreen onStart={handleStartScorecard} />;
      case 'scorecard':
        return <ScorcardForm onComplete={handleScorcardComplete} />;
      case 'results':
        return <ResultsScreen scorecardData={scorecardData} onRestart={handleRestart} onNavigate={handleNavigationSelect} />;
      case 'publishing-selector':
        return <PublishingPathSelector onComplete={handlePublishingComplete} />;
      case 'publishing-results':
        return <PublishingResults responses={publishingResponses} scores={publishingScores} recommendedPath={recommendedPath} confidence={confidence} onRestart={handleRestart} />;
      case 'action-plan-builder':
        return <ActionPlanBuilder onComplete={handleActionPlanComplete} />;
      case 'action-plan-results':
        return <ActionPlanResults responses={planResponses} plan={actionPlan} onRestart={handleRestart} />;
      default:
        return <NavigationScreen onSelect={handleNavigationSelect} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 relative">
      {/* Logout button (shown when authenticated) */}
      {isAuthenticated && currentStep !== 'auth' && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={handleLogout}
          className="fixed top-4 right-4 z-50 px-3 py-2 bg-white/80 backdrop-blur-sm hover:bg-white text-gray-600 hover:text-gray-900 rounded-lg shadow-sm text-sm font-medium transition-all duration-300"
        >
          Logout
        </motion.button>
      )}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
        >
          {renderContent()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default App;